import React, { useState, useEffect, useRef } from 'react';
import { Sparkle, CheckCircle, UsersThree, CaretDown, CaretUp } from '@phosphor-icons/react';
import { MealSlotDisplay } from './utils';
import OccasionMenusSection, { ConflictResolutionModal } from './OccasionMenus';
import SmartTextInput from './SmartTextInput';
import {
  SLOT_PILLS, STYLE_PILLS, STYLE_DEFAULT_EXTRAS,
  EXTRA_PILLS, OCCASION_PILLS, CUISINE_PILLS,
  AVOID_QUICK_CHIPS, INSTRUCTION_QUICK_CHIPS, INSTRUCTION_TEMPLATES,
} from './constants';

function AdvancedOptionsPanel({ open, onToggle, adv, setAdv, api, contextHints }) {
  const toggleSet = (key, val) => setAdv(prev => {
    const s = new Set(prev[key]);
    if (s.has(val)) s.delete(val); else s.add(val);
    return { ...prev, [key]: s };
  });

  const applyStyle = (style) => setAdv(prev => ({
    ...prev,
    meal_style: style,
    course_lunch: new Set(STYLE_DEFAULT_EXTRAS[style]),
    course_dinner: new Set(STYLE_DEFAULT_EXTRAS[style]),
  }));

  return (
    <div className="mb-4 rounded-xl overflow-hidden" data-testid="ai-advanced-panel"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <button onClick={onToggle} data-testid="ai-advanced-toggle"
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-white/5 transition-colors">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkle size={13} className="text-purple-300" />
          <span className="text-xs font-bold text-neutral-300">Advanced options</span>
          <span className="text-[10px] text-neutral-500 truncate">
            {adv.slots.size} slots · {adv.meal_style} · {adv.occasion_type !== 'regular' ? `${adv.occasion_type} · ` : ''}{adv.cuisine_priority.size > 0 ? `${adv.cuisine_priority.size} cuisines` : 'no cuisine tilt'}
          </span>
        </div>
        {open ? <CaretUp size={11} className="text-neutral-400" /> : <CaretDown size={11} className="text-neutral-400" />}
      </button>

      {open && (
        <div className="border-t border-white/5 p-4 space-y-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Meals to plan</p>
            <div className="flex flex-wrap gap-1.5">
              {SLOT_PILLS.map(s => {
                const on = adv.slots.has(s.key);
                return (
                  <button key={s.key} type="button" onClick={() => toggleSet('slots', s.key)}
                    data-testid={`ai-slot-${s.key}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold border ${on
                      ? 'bg-[#FF6B35]/20 text-[#FF6B35] border-[#FF6B35]/40'
                      : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'}`}>
                    {s.emoji} {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Lunch &amp; dinner style</p>
            <div className="flex flex-wrap gap-1.5">
              {STYLE_PILLS.map(s => {
                const on = adv.meal_style === s.key;
                return (
                  <button key={s.key} type="button" onClick={() => applyStyle(s.key)}
                    data-testid={`ai-style-${s.key}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold border text-left ${on
                      ? 'bg-purple-500/20 text-purple-200 border-purple-500/40'
                      : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'}`}
                    title={s.desc}>
                    {s.emoji} {s.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-neutral-500 mt-1.5 italic">{STYLE_PILLS.find(s => s.key === adv.meal_style)?.desc}</p>
          </div>

          {(adv.slots.has('lunch') || adv.slots.has('dinner')) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {adv.slots.has('lunch') && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Lunch course extras</p>
                  <div className="flex flex-wrap gap-1">
                    {EXTRA_PILLS.map(e => {
                      const on = adv.course_lunch.has(e.key);
                      return (
                        <button key={e.key} type="button" onClick={() => toggleSet('course_lunch', e.key)}
                          data-testid={`ai-lunch-extra-${e.key}`}
                          className={`px-2 py-1 rounded-full text-[10px] font-bold border ${on
                            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                            : 'bg-white/5 text-neutral-400 border-white/10'}`}>
                          {e.emoji} {e.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {adv.slots.has('dinner') && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Dinner course extras</p>
                  <div className="flex flex-wrap gap-1">
                    {EXTRA_PILLS.map(e => {
                      const on = adv.course_dinner.has(e.key);
                      return (
                        <button key={e.key} type="button" onClick={() => toggleSet('course_dinner', e.key)}
                          data-testid={`ai-dinner-extra-${e.key}`}
                          className={`px-2 py-1 rounded-full text-[10px] font-bold border ${on
                            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                            : 'bg-white/5 text-neutral-400 border-white/10'}`}>
                          {e.emoji} {e.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Occasion / week type</p>
            <div className="flex flex-wrap gap-1.5">
              {OCCASION_PILLS.map(o => {
                const on = adv.occasion_type === o.key;
                return (
                  <button key={o.key} type="button" onClick={() => setAdv(prev => ({ ...prev, occasion_type: o.key }))}
                    data-testid={`ai-occasion-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-bold border ${on
                      ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                      : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'}`}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Cuisine priority (tilt towards)</p>
            <div className="flex flex-wrap gap-1">
              {CUISINE_PILLS.map(c => {
                const on = adv.cuisine_priority.has(c);
                return (
                  <button key={c} type="button" onClick={() => toggleSet('cuisine_priority', c)}
                    data-testid={`ai-cuisine-${c.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${on
                      ? 'bg-pink-500/15 text-pink-200 border-pink-500/40'
                      : 'bg-white/5 text-neutral-400 border-white/10'}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">Cook availability</p>
              <input value={adv.cook_availability} onChange={e => setAdv(prev => ({ ...prev, cook_availability: e.target.value }))}
                placeholder='e.g., "Cook on leave Mon-Tue"' data-testid="ai-cook-availability"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-[12px] text-white outline-none" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">Avoid this week</p>
              <SmartTextInput
                value={adv.weekly_avoid}
                onChange={(v) => setAdv(prev => ({ ...prev, weekly_avoid: v }))}
                api={api}
                field="avoid"
                placeholder='e.g., "no paneer, no spinach"'
                quickChips={AVOID_QUICK_CHIPS}
                contextChips={[
                  ...((contextHints?.avoid?.low_rated) || []),
                  ...((contextHints?.avoid?.over_used) || []),
                  ...((contextHints?.avoid?.never) || []),
                  ...((contextHints?.avoid?.allergies) || []),
                ]}
                dataTestid="ai-weekly-avoid"
                compact
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIChefTab({ api, prefs, onUpdate, pendingAction, clearPendingAction, onGoToPlanner }) {
  const [aiLoading, setAiLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [extraPrefs, setExtraPrefs] = useState('');
  const [adminBusy, setAdminBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [generateImages, setGenerateImages] = useState(true);

  const runAdminCleanup = async () => {
    if (!window.confirm('Clean test data?\n\nThis will delete every TEST_* / test_* recipe, rating, dish-pref and strip them from meal plans. Continue?')) return;
    setAdminBusy(true);
    try {
      const dryRun = await api.post('/meals/admin/cleanup-test-data', { dry_run: true });
      const d = dryRun.data || {};
      const total = (d.deleted?.dish_ratings || 0) + (d.deleted?.recipes || 0) + (d.updated?.meal_plans || 0) + (d.updated?.food_preferences || 0);
      if (total === 0) {
        window.alert('No TEST_* data found — already clean.');
      } else {
        if (!window.confirm(`Found:\n• ${d.deleted?.dish_ratings || 0} ratings\n• ${d.deleted?.recipes || 0} recipes\n• ${d.updated?.meal_plans || 0} meal-plan days\n• ${d.updated?.food_preferences || 0} food-preference docs\n\nDelete now?`)) {
          setAdminBusy(false);
          return;
        }
        await api.post('/meals/admin/cleanup-test-data', { dry_run: false });
        window.alert('Cleanup complete. AI Chef will now ignore old test dishes.');
      }
      onUpdate && onUpdate();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Cleanup failed.';
      window.alert(typeof msg === 'string' ? msg : 'Cleanup failed. (admin only)');
    }
    setAdminBusy(false);
  };
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().slice(0, 10);
  });
  const [eaterProfiles, setEaterProfiles] = useState([]);
  const [selectedProfileIds, setSelectedProfileIds] = useState(new Set());
  const [splitMode, setSplitMode] = useState(false);
  const [daysToGen, setDaysToGen] = useState(7);
  const [conflict, setConflict] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);
  const [contextHints, setContextHints] = useState(null);
  const [adv, setAdv] = useState({
    slots: new Set(['breakfast', 'lunch', 'dinner', 'snacks']),
    meal_style: 'standard',
    course_lunch: new Set(['raita']),
    course_dinner: new Set(['raita']),
    occasion_type: 'regular',
    cook_availability: '',
    cuisine_priority: new Set(),
    weekly_avoid: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/meals/eater-profiles');
        if (cancelled) return;
        const list = r.data || [];
        setEaterProfiles(list);
        setSelectedProfileIds(new Set(list.filter(p => p.active !== false).map(p => p.profile_id)));
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Load contextual chip suggestions (low-rated dishes, birthdays, festivals, season)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/meals/ai-chef/context-hints', {
          params: { start_date: startDate, days: daysToGen },
        });
        if (!cancelled) setContextHints(r.data || null);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [api, startDate, daysToGen]);

  const [briefGuestSeed, setBriefGuestSeed] = useState(null);
  const consumedBriefRef = useRef(null);

  useEffect(() => {
    if (!pendingAction) return;
    // Dedupe — StrictMode invokes effects twice in dev; this also guards against re-renders
    if (consumedBriefRef.current === pendingAction.brief_id) return;
    consumedBriefRef.current = pendingAction.brief_id;

    const t = pendingAction?.action?.type;
    if (t === 'open_aichef') {
      const extra = pendingAction.action.extra_preferences || '';
      if (extra) {
        setExtraPrefs(prev => prev ? (prev + ' ' + extra) : extra);
      }
      setTimeout(() => {
        const el = document.querySelector('[data-testid="generate-meal-plan-btn"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else if (t === 'open_occasion_guest') {
      setBriefGuestSeed({ guest_id: pendingAction.action.guest_id, brief_id: pendingAction.brief_id });
    }
    clearPendingAction && clearPendingAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAction]);

  const toggleProfileId = (id) => {
    setSelectedProfileIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleGenerate = async (conflict_resolution = "") => {
    setAiLoading(true);
    setResult(null);
    setConflict(null);
    try {
      const payload = {
        start_date: startDate,
        extra_preferences: extraPrefs,
        days: Number(daysToGen) || 7,
        slots: Array.from(adv.slots),
        meal_style: adv.meal_style,
        course_extras: {
          lunch: Array.from(adv.course_lunch),
          dinner: Array.from(adv.course_dinner),
        },
        occasion_type: adv.occasion_type,
        cook_availability: adv.cook_availability,
        cuisine_priority: Array.from(adv.cuisine_priority),
        weekly_avoid: adv.weekly_avoid,
      };
      if (conflict_resolution) payload.conflict_resolution = conflict_resolution;
      if (splitMode && selectedProfileIds.size >= 2) {
        payload.profile_ids = Array.from(selectedProfileIds);
      }
      payload.generate_images = generateImages;

      const startRes = await api.post('/meals/jobs/menu', payload);
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job id returned');
      setJob({ job_id: jobId, status: 'queued', progress: 0, step_label: 'Starting…' });

      let lastJob = null;
      let polls = 0;
      while (polls < 600) {
        await new Promise(r => setTimeout(r, 2000));
        polls += 1;
        let s;
        try {
          s = await api.get(`/meals/jobs/${jobId}`);
        } catch (e) {
          continue;
        }
        lastJob = s.data;
        setJob(lastJob);
        if (lastJob.status === 'done' || lastJob.status === 'failed' || lastJob.status === 'cancelled') break;
      }
      if (lastJob?.status === 'done') {
        setResult({ ok: true, plans_count: lastJob.result?.plans_count, images_queued: lastJob.result?.images_queued || 0 });
        onUpdate();
      } else if (lastJob?.status === 'cancelled') {
        setResult({ error: 'Cancelled.' });
      } else {
        const err = lastJob?.error || lastJob?.step_label || 'Job failed';
        if (err && err.includes('plans_exist')) {
          try {
            const m = err.match(/conflict_dates['"]?:\s*\[([^\]]+)\]/);
            const dates = m ? m[1].split(',').map(s => s.replace(/['"\s]/g, '')) : [];
            setConflict({ conflict_dates: dates, message: 'Some dates already have plans' });
          } catch (_) {
            setResult({ error: err });
          }
        } else {
          setResult({ error: err });
        }
      }
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.detail?.code === 'plans_exist') {
        const detail = err.response.data.detail;
        setConflict({
          conflict_dates: detail.conflict_dates || [],
          message: detail.message,
        });
      } else {
        setResult({ error: err.response?.data?.detail || 'Generation failed. Check OpenAI API key in Settings.' });
      }
    }
    setAiLoading(false);
    setJob(null);
  };

  const handleCancel = async () => {
    if (!job?.job_id) return;
    try {
      await api.post(`/meals/jobs/${job.job_id}/cancel`);
    } catch (_) { /* ignore */ }
  };

  const seedDefaults = async () => {
    try {
      await api.post('/meals/eater-profiles/seed-defaults');
      const r = await api.get('/meals/eater-profiles');
      setEaterProfiles(r.data || []);
      setSelectedProfileIds(new Set((r.data || []).map(p => p.profile_id)));
      setSplitMode(true);
    } catch (_) { /* ignore */ }
  };

  const profileSummary = prefs ? [
    prefs.diet_type,
    `Spice: ${prefs.spice_level || 'medium'}`,
    (prefs.cuisine_preferences || []).length > 0 ? `Cuisines: ${prefs.cuisine_preferences.join(', ')}` : null,
    (prefs.health_goals || []).length > 0 ? `Goals: ${prefs.health_goals.join(', ')}` : null,
    Object.keys(prefs.ingredient_prefs || {}).length > 0 ? `${Object.keys(prefs.ingredient_prefs).length} ingredient prefs` : null,
    Object.keys(prefs.dish_prefs || {}).length > 0 ? `${Object.keys(prefs.dish_prefs).length} dish prefs` : null,
    (prefs.frequency_rules || []).length > 0 ? `${prefs.frequency_rules.length} frequency rules` : null,
  ].filter(Boolean) : [];

  return (
    <div className="space-y-4">
      <OccasionMenusSection api={api} eaterProfiles={eaterProfiles} onSeedDefaults={seedDefaults}
        briefGuestSeed={briefGuestSeed} clearBriefGuestSeed={() => setBriefGuestSeed(null)} />

      <div className="rounded-2xl p-4 sm:p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkle size={20} weight="fill" className="text-[#FF6B35]" />
            <span className="text-sm font-bold">AI Chef</span>
          </div>
          <button
            onClick={runAdminCleanup}
            disabled={adminBusy}
            data-testid="aichef-cleanup-test-data-btn"
            title="Admin: purge any leftover TEST_*/test_* dishes from ratings, recipes, prefs and plans so the AI never sees them again."
            className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30 disabled:opacity-50"
          >
            {adminBusy ? 'Cleaning…' : 'Clean test data'}
          </button>
        </div>
        <p className="text-xs text-neutral-400 mb-4">Generate a personalized 1–30 day menu based on your complete food profile, past ratings, and recent meals. The AI avoids repetitions and respects all your preferences.</p>

        {profileSummary.length > 0 && (
          <div className="rounded-xl p-3 mb-4" style={{ background: 'rgba(255,107,53,0.05)', border: '1px solid rgba(255,107,53,0.1)' }}>
            <p className="text-[10px] text-[#FF6B35] font-semibold mb-1">AI will use your Food Profile:</p>
            <p className="text-[11px] text-neutral-400">{profileSummary.join(' | ')}</p>
          </div>
        )}

        <div className="rounded-xl p-3 mb-4" style={{ background: 'rgba(168,85,247,0.05)', border: '1px solid rgba(168,85,247,0.15)' }} data-testid="multi-eater-card">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <UsersThree size={14} weight="fill" className="text-purple-400" />
              <span className="text-[11px] font-bold text-purple-300 uppercase tracking-wider">Multi-eater mode</span>
            </div>
            {eaterProfiles.length === 0 ? (
              <button onClick={seedDefaults} data-testid="seed-defaults-btn"
                className="text-[10px] font-bold px-2.5 py-1 rounded-lg bg-purple-500/20 text-purple-200 border border-purple-500/40 hover:bg-purple-500/30">
                + Add Kids & Adults profiles
              </button>
            ) : (
              <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="split-toggle">
                <input type="checkbox" checked={splitMode}
                  onChange={e => setSplitMode(e.target.checked)}
                  className="accent-purple-500" />
                <span className="text-[11px] font-semibold text-neutral-300">Plan separately per profile</span>
              </label>
            )}
          </div>
          {eaterProfiles.length > 0 ? (
            splitMode ? (
              <div className="space-y-1.5">
                <p className="text-[10px] text-neutral-500">Tick which profiles to plan for. AI will share dishes when possible; split only when needed.</p>
                <div className="flex flex-wrap gap-1.5">
                  {eaterProfiles.map(p => {
                    const sel = selectedProfileIds.has(p.profile_id);
                    return (
                      <button key={p.profile_id} type="button" onClick={() => toggleProfileId(p.profile_id)}
                        data-testid={`profile-chip-${p.profile_id}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-all inline-flex items-center gap-1`}
                        style={{
                          background: sel ? `${p.color || '#A855F7'}25` : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${sel ? (p.color || '#A855F7') + '70' : 'rgba(255,255,255,0.08)'}`,
                          color: sel ? (p.color || '#A855F7') : '#a3a3a3',
                        }}>
                        <span>{p.icon || '🧑'}</span> {p.name}
                        <span className="text-[9px] opacity-70">· {p.spice_level}</span>
                      </button>
                    );
                  })}
                </div>
                {selectedProfileIds.size < 2 && (
                  <p className="text-[10px] text-amber-400 mt-1">Select 2+ profiles to actually split. Otherwise AI Chef will treat it as a single-eater plan.</p>
                )}
              </div>
            ) : (
              <p className="text-[10px] text-neutral-500">Currently planning <span className="font-bold text-neutral-300">as one family</span>. {eaterProfiles.length} profile{eaterProfiles.length !== 1 ? 's' : ''} available — toggle the switch to plan different dishes per kid/adult.</p>
            )
          ) : (
            <p className="text-[10px] text-neutral-500">No eater profiles yet. Add "Kids" + "Adults" profiles (or per-person) to plan different dishes for different family members.</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Plan starting</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="ai-start-date"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Extra Instructions (optional)</label>
            <SmartTextInput
              value={extraPrefs}
              onChange={setExtraPrefs}
              api={api}
              field="instructions"
              placeholder="e.g., festive week, guests coming Sunday..."
              quickChips={INSTRUCTION_QUICK_CHIPS}
              contextChips={[
                ...((contextHints?.instructions?.birthdays) || []),
                ...((contextHints?.instructions?.events) || []),
                ...((contextHints?.instructions?.season) || []),
              ]}
              templates={INSTRUCTION_TEMPLATES}
              dataTestid="ai-extra-prefs"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Plan for how many days?</label>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="ai-days-picker">
            {[1, 3, 5, 7, 14].map(n => (
              <button key={n} type="button" onClick={() => setDaysToGen(n)}
                data-testid={`ai-days-preset-${n}`}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-all ${daysToGen === n
                  ? 'bg-[#FF6B35]/20 text-[#FF6B35] border-[#FF6B35]/40'
                  : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'}`}>
                {n} day{n === 1 ? '' : 's'}
              </button>
            ))}
            <input type="number" min="1" max="30" value={daysToGen}
              onChange={e => {
                const v = parseInt(e.target.value || '0', 10);
                if (Number.isFinite(v)) setDaysToGen(Math.max(1, Math.min(30, v)));
              }}
              data-testid="ai-days-custom"
              className="w-20 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[11px] text-white outline-none focus:border-[#FF6B35]" />
            <span className="text-[10px] text-neutral-500">days (1-30)</span>
          </div>
        </div>

        <AdvancedOptionsPanel open={advOpen} onToggle={() => setAdvOpen(o => !o)} adv={adv} setAdv={setAdv} api={api} contextHints={contextHints} />

        <button onClick={() => handleGenerate("")} disabled={aiLoading} data-testid="generate-meal-plan-btn"
          className="px-6 py-3 rounded-2xl bg-[#FF6B35] text-white text-sm font-bold flex items-center gap-2 btn-glow disabled:opacity-50">
          <Sparkle size={16} weight="fill" /> {aiLoading ? 'Generating...' : `Generate ${daysToGen}-Day Menu`}
        </button>

        <label className="mt-3 flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-white/5" data-testid="generate-images-toggle">
          <input type="checkbox" checked={generateImages} onChange={e => setGenerateImages(e.target.checked)}
            className="mt-0.5 accent-[#FF6B35]" />
          <div className="flex-1">
            <p className="text-[12px] text-white font-semibold">Also generate dish images</p>
            <p className="text-[10px] text-neutral-500">AI creates a photo for every NEW dish (cached forever). Adds ~30s per missing image.</p>
          </div>
        </label>
      </div>

      {aiLoading && job && (
        <div className="rounded-2xl p-4 space-y-3" data-testid="menu-job-progress"
          style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.08), rgba(255,107,53,0.02))', border: '1px solid rgba(255,107,53,0.30)' }}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-4 h-4 border-2 border-[#FF6B35]/40 border-t-[#FF6B35] rounded-full animate-spin flex-shrink-0" />
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#FF6B35]">AI is working</p>
            </div>
            <button onClick={handleCancel} data-testid="menu-job-cancel"
              className="text-[10px] font-bold text-neutral-400 hover:text-red-300 underline">Cancel</button>
          </div>
          <div>
            <p className="text-sm text-white font-semibold mb-1.5">{job.step_label}</p>
            {job.current_item && <p className="text-[11px] text-neutral-400 truncate">📸 {job.current_item}</p>}
          </div>
          <div className="w-full bg-white/5 rounded-full h-1.5 overflow-hidden">
            <div className="bg-gradient-to-r from-[#FF6B35] to-[#F59E0B] h-1.5 rounded-full transition-all duration-500"
              style={{ width: `${Math.max(2, job.progress || 0)}%` }} />
          </div>
          <div className="flex items-center justify-between text-[10px] text-neutral-500">
            <span>{job.total_items > 0 ? `${Math.min(job.total_items, Math.round(((job.progress - 70) / 28) * job.total_items))}/${job.total_items} images` : 'Preparing…'}</span>
            <span className="font-mono">{job.progress || 0}%</span>
          </div>
        </div>
      )}

      {conflict && (
        <ConflictResolutionModal
          conflict={conflict}
          onChoose={(resolution) => { setConflict(null); if (resolution) handleGenerate(resolution); }}
          onCancel={() => setConflict(null)}
        />
      )}

      {result && result.ok && (
        <div className="rounded-2xl p-4" data-testid="menu-job-result-ok"
          style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-emerald-400" weight="fill" />
              <p className="text-sm font-semibold text-emerald-300">
                Menu generated · {result.plans_count || 0} day(s) added to This Week.
              </p>
            </div>
            {onGoToPlanner && (
              <button onClick={onGoToPlanner} data-testid="goto-this-week-btn"
                className="px-3.5 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-xs font-bold hover:bg-emerald-500/25">
                View menu → This Week
              </button>
            )}
          </div>
          <p className="text-[11px] text-neutral-400 mt-1">
            {result.images_queued > 0
              ? `${result.images_queued} dish photo(s) painting in the background (~${Math.max(1, Math.ceil(result.images_queued * 18 / 8 / 60))} min) — they'll appear on the menu as they finish, even if you leave this page. Open This Week to watch the live countdown.`
              : "Dish photos are painting in the background — they'll appear on the menu as they finish, even if you leave this page."}
          </p>
        </div>
      )}

      {result && result.error && (
        <div className="rounded-2xl p-4" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)' }}>
          <p className="text-sm text-red-400">{result.error}</p>
        </div>
      )}

      {result && result.plans && result.plans.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-green-400" />
            <p className="text-sm font-semibold text-green-400">{result.message}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {result.plans.map((p, i) => (
              <div key={i} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-sm font-semibold mb-2">{p.date}</p>
                <div className="space-y-1.5 text-[12px]">
                  {p.breakfast && <div><span className="text-[9px] uppercase text-neutral-500 block">Breakfast</span><MealSlotDisplay value={p.breakfast} /></div>}
                  {p.lunch && <div><span className="text-[9px] uppercase text-neutral-500 block">Lunch</span><MealSlotDisplay value={p.lunch} /></div>}
                  {p.dinner && <div><span className="text-[9px] uppercase text-neutral-500 block">Dinner</span><MealSlotDisplay value={p.dinner} /></div>}
                  {p.snacks && <div><span className="text-[9px] uppercase text-neutral-500 block">Snacks</span><MealSlotDisplay value={p.snacks} /></div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result && result.raw_suggestion && (
        <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs font-semibold text-neutral-400 mb-2">AI Suggestion (raw)</p>
          <pre className="text-xs text-neutral-400 whitespace-pre-wrap">{result.raw_suggestion}</pre>
        </div>
      )}
    </div>
  );
}
